import { VisibilityBadge } from '@/components/shared';
import { CloneTemplateDialog } from '@/components/templates';
import type { Template } from '@/config/objects/templates';
import { TEMPLATE_TABS } from '@/pages/templates/templateTabs';
import {
  Button,
  ConfirmDialog,
  EntityDetailShell,
  toast,
  useApiClient,
} from '@startupquickstart/frontend';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Copy, FileText, Pencil, Star, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

export function TemplateDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const apiClient = useApiClient();
  const queryClient = useQueryClient();
  const [pendingDelete, setPendingDelete] = useState<Template | null>(null);
  const [pendingClone, setPendingClone] = useState<Template | null>(null);
  const basePath = `/templates/${id}`;

  const deleteMutation = useMutation({
    mutationFn: async (
      templateId: string,
    ): Promise<{ success: boolean; error?: string }> => {
      return apiClient.delete(`/templates/${templateId}`);
    },
    onSuccess: (response: { success: boolean; error?: string }) => {
      if (response.success) {
        queryClient.invalidateQueries({ queryKey: ['Template'] });
        navigate('/templates');
      } else {
        throw new Error(response.error || 'Failed to delete template');
      }
    },
  });

  const handleDelete = () => {
    if (!pendingDelete) return;
    toast.promise(deleteMutation.mutateAsync(pendingDelete.id), {
      loading: 'Deleting template...',
      success: 'Template deleted successfully',
      error: (error: Error) => error?.message || 'Failed to delete template',
    });
  };

  return (
    <>
      <EntityDetailShell<Template>
        resource="templates"
        queryKey="Template"
        id={id}
        basePath={basePath}
        entityLabel="Template"
        tabs={TEMPLATE_TABS}
        contextKey="template"
        hero={{
          category: { label: 'Game Template', icon: FileText },
          title: (t) => t?.name || 'Template',
          fallbackIcon: FileText,
          badges: (t) => (
            <>
              {t.visibility && <VisibilityBadge visibility={t.visibility} variant="solid" />}
              {t.isFeatured && (
                <span className="inline-flex items-center gap-1 rounded-full bg-amber-500 px-2.5 py-0.5 text-xs font-medium text-white">
                  <Star className="h-3 w-3" />
                  Featured
                </span>
              )}
              {t.estimatedDuration && (
                <span className="text-sm text-white/70">{t.estimatedDuration}</span>
              )}
            </>
          ),
        }}
        breadcrumbs={(t) => [
          { label: 'Home', href: '/' },
          { label: 'Game Templates', href: '/templates' },
          { label: t?.name || 'Template', current: true },
        ]}
        headerActions={(t) =>
          t ? (
            <>
              <Button variant="outline" onClick={() => setPendingClone(t)}>
                <Copy className="h-4 w-4" />
                Clone
              </Button>
              <Button variant="outline" onClick={() => navigate(`${basePath}/edit`)}>
                <Pencil className="h-4 w-4" />
                Edit
              </Button>
              <Button variant="destructive" onClick={() => setPendingDelete(t)}>
                <Trash2 className="h-4 w-4" />
                Delete
              </Button>
            </>
          ) : null
        }
      />

      <ConfirmDialog
        open={!!pendingDelete}
        onOpenChange={(open) => !open && setPendingDelete(null)}
        title="Delete Template"
        description={`Are you sure you want to delete "${pendingDelete?.name}"? This action cannot be undone.`}
        confirmText="Delete"
        variant="destructive"
        loading={deleteMutation.isPending}
        onConfirm={handleDelete}
      />

      {pendingClone && (
        <CloneTemplateDialog
          templateId={pendingClone.id}
          templateName={pendingClone.name}
          open={!!pendingClone}
          onOpenChange={(open) => !open && setPendingClone(null)}
        />
      )}
    </>
  );
}
